<template>
  <div>
    <button
      type="button"
      :aria-expanded="showStoreInfos.toString()"
      class="awSrOnly"
      @click="showStoreInfos = !showStoreInfos"
      v-text="$awt('aw.map.delivery.show_store_list')"
    />
    <ul
      v-if="showStoreInfos && (type === TYPE_OSM)"
      :class="$style.storeInformation"
      class="awSrOnly"
    >
      <li v-for="dp in departmentStores" :key="dp.id" tabindex="0">
        <div v-html="popupTemplate(dp)" />
      </li>
    </ul>
  </div>
</template>

<script>
  import { mapState } from 'pinia';
  import { nextTick } from 'vue';
  import { CLICK_MAP_TYPE_DEPARTMENT_STORE, PUSH_CLICK_MAP } from '~~/common/plugins/aw-analytics.js';
  import AddressFormatter from '~~/common/mixins/addressFormatter';
  import { useDeliveryStore } from '~~/shop/app/stores/delivery';
  import { useStoresAndPetrolsStore } from '~~/common/stores/storesAndPetrolsStore';
  import { getOSMConfig } from '~~/shop/app/config/MapConfig';
  import { useRuntimeConfig } from 'nuxt/app';

  export const TYPE_OSM = 'osm';
  const MILLISEC_TIMEOUT = 800;

  export default {
    name: 'AwStoreMap',
    mixins: [AddressFormatter],
    props: {
      type: {
        type: String,
        required: true,
        validator: t => [TYPE_OSM].includes(t),
      },
      modelValue: {
        type: Array,
        required: true,
        validator: boundsValidator,
      },
      triggerClickId: {
        type: String,
        required: false,
        default: '',
      },
      hasTooltip: {
        type: Boolean,
        required: false,
        default: true,
      },
      selectedStoreId: {
        type: String,
        required: false,
        default: null,
      },
    },
    emits: [
      'visible-department-stores',
      'select-store',
      'update:modelValue',
    ],
    data () {
      return {
        TYPE_OSM,
        leaflet: null,
        map: null,
        dpMarkers: [],
        idTimeoutVisibleStoreChange: null,
        idTimeoutBoundsChanged: null,
        idRafInvalidateSize: null,
        resizeObserver: null,
        isDestroyed: false,
        showStoreInfos: false,
      };
    },
    computed: {
      ...mapState(useStoresAndPetrolsStore, {
        petrolStations (state) {
          return state.petrolStations;
        },
      }),
      ...mapState(useDeliveryStore, {
        departmentStores (state) {
          return state.departmentStores;
        },
        deliveryMethod: state => state.setup,
        visibleDepartmentStores (state) {
          return state.departmentStores.filter((store) => {
            const coords = store.address.coordinates;
            return coords && ![
              this.modelValue[0]?.latitude < coords.latitude,
              this.modelValue[0]?.longitude < coords.longitude,
              this.modelValue[1]?.latitude > coords.latitude,
              this.modelValue[1]?.longitude > coords.longitude,
            ].includes(false);
          });
        },
      }),
    },
    watch: {
      triggerClickId (newValue, oldValue) {
        if (newValue !== oldValue) {
          const mark = this.dpMarkers[newValue];
          if (mark) {
            if (this.type === TYPE_OSM) {
              mark.fire('click');
            }
          }
        }
      },
      modelValue () {
        const bounds = new this.leaflet.LatLngBounds(this.latLng(this.modelValue[0]), this.latLng(this.modelValue[1]));
        if (!bounds.equals(this.map.getBounds())) {
          this.map.fitBounds(bounds);
        }
        // -
        clearTimeout(this.idTimeoutVisibleStoreChange);
        this.idTimeoutVisibleStoreChange = setTimeout(() => {
          this.$emit('visible-department-stores', this.visibleDepartmentStores);
        }, MILLISEC_TIMEOUT);
      },
    },
    mounted () {
      if (this.type === TYPE_OSM) {
        this.createOSM();
      }
      nextTick(() => {
        setTimeout(() => {
          this.$el.setAttribute('tabindex', '-1');
        }, 1000);
      });
    },
    // Leaflet keeps `_leaflet_id` on the container and listeners on window, which
    // both breaks re-init on a reused element and pins this component in memory.
    beforeUnmount () {
      this.isDestroyed = true;
      clearTimeout(this.idTimeoutVisibleStoreChange);
      clearTimeout(this.idTimeoutBoundsChanged);
      cancelAnimationFrame(this.idRafInvalidateSize);
      this.resizeObserver?.disconnect();
      this.map?.remove();
    },
    methods: {
      popupTemplate (dp, withNameRow = true) {
        const DAYS_OF_WEEK = {
          0: 'monday',
          1: 'tuesday',
          2: 'wednesday',
          3: 'thursday',
          4: 'friday',
          5: 'saturday',
          6: 'sunday',
        };

        let serviceHours = '';
        if (dp.serviceHours) {
          Object.keys(DAYS_OF_WEEK).forEach((day) => {
            const currentDayOpenings = Object.values(dp.serviceHours).find(sh => sh.dayOfWeek === parseInt(day));
            const ariaLabel = `${this.$awt('days.' + DAYS_OF_WEEK[day])} ${currentDayOpenings ? (currentDayOpenings.from + ' - ' + currentDayOpenings.to) : this.$awt('openings.closed')}.`;
            serviceHours += `<div class="${this.$style.openingHoursRow}" aria-label="${ariaLabel}">
                            <span>${this.$awt('days.' + DAYS_OF_WEEK[day])}:</span>
                            <span>${currentDayOpenings ? (currentDayOpenings.from + ' - ' + currentDayOpenings.to) : this.$awt('openings.closed')}</span>
                           </div>`;
          });
        }

        const nameRow = `<div><b aria-label="${this.$awt('aw.map.delivery.store.store_name_label', { store_name: dp.name })}"  class="${this.$style.nameRow}">${dp.name}</b></div>`;
        const dpAddress = typeof dp.address === 'string' ? dp.address : this.formatAddress(dp.address);
        return `
          <div>
            ${withNameRow ? nameRow : ''}
            <span aria-label="${this.$awt('aw.map.delivery.store.address_label', { address: dpAddress })}" class="${this.$style.addressRow}">${dpAddress}</span>
            <h6  class="awSrOnly">${this.$awt('aw.map.delivery.store.opening_hours_label')}</h6>
            ${serviceHours}
          </div>
        `;
      },
      /**
       * \@context {/api/v2/contexts/DepartmentStore}
       * \@type {CoordinatesModel}
       */
      latLng ({
        latitude,
        longitude,
      }) {
        if (this.type === TYPE_OSM) {
          return new this.leaflet.LatLng(parseFloat(latitude), parseFloat(longitude));
        }
      },
      async cluster (markers) {
        if (this.type === TYPE_OSM) {
          const { MarkerClusterGroup } = await import('leaflet.markercluster');
          const mcg = new MarkerClusterGroup({
            iconCreateFunction: (cluster) => {
              return new this.leaflet.DivIcon({
                html: cluster.getChildCount(),
                className: this.$style.mapMarkerClustererCustom,
              });
            },
          });
          for (const m of markers) {
            mcg.addLayer(m);
          }
          this.map.addLayer(mcg);
        }
      },
      async createMarkers () {
        const config = useRuntimeConfig();
        const OSMConfig = getOSMConfig(config);
        const deliveryStore = useDeliveryStore();
        await deliveryStore.fetchDepartmentStores();
        clearTimeout(this.idTimeoutVisibleStoreChange);
        this.idTimeoutVisibleStoreChange = setTimeout(() => {
          this.$emit('visible-department-stores', this.departmentStores);
        }, MILLISEC_TIMEOUT);

        if (this.type === TYPE_OSM) {
          const storeList = [...this.departmentStores, ...this.petrolStations];
          const filteredStoreList = this.selectedStoreId ? storeList.filter(store => store.id === this.selectedStoreId) : storeList;
          return Object.fromEntries(filteredStoreList.map((dp) => {
            const m = new this.leaflet.Marker(this.latLng(dp.address.coordinates || {
              latitude: 0,
              longitude: 0,
            }), {
              icon: new this.leaflet.Icon(OSMConfig.icon),
              title: this.selectedStoreId ? '' : dp.name,
            });
            if (this.hasTooltip) {
              m.bindPopup(this.popupTemplate(dp));
            }
            m.on('click', this.clickHandler.bind(this, dp));
            return [dp.id, m];
          }));
        }
      },
      clickHandler (dp) {
        this.$emit('select-store', dp);
        try {
          this.$awAnalytics[PUSH_CLICK_MAP]({
            clickType: CLICK_MAP_TYPE_DEPARTMENT_STORE,
            mapAreas: [dp.name],
          });
        } catch (err) {
          this.$logger.error(err);
        }
      },
      async createOSM () {
        const config = useRuntimeConfig();
        const OSMConfig = getOSMConfig(config);
        this.leaflet = await import('leaflet');
        if (this.isDestroyed || this.map) {
          return;
        }
        this.map = (new this.leaflet.Map(this.$el))
          .fitBounds(new this.leaflet.LatLngBounds(this.latLng(this.modelValue[0]), this.latLng(this.modelValue[1])))
        ;

        (new this.leaflet.TileLayer(...OSMConfig.tileLayer)).addTo(this.map);
        this.map.zoomControl.remove();

        this.leaflet.control.zoom({
          zoomInTitle: this.$awt('aw.map.zoom_control.zoom_in_title'),
          zoomOutTitle: this.$awt('aw.map.zoom_control.zoom_out_title'),
        }).addTo(this.map);

        this.dpMarkers = await this.createMarkers();
        this.cluster(Object.values(this.dpMarkers));
        this.map.fitBounds(new this.leaflet.LatLngBounds(this.latLng(this.modelValue[0]), this.latLng(this.modelValue[1])));
        this.map?.setZoom(this.map?.getZoom() - 6);
        this.onBoundsChanged();
        this.observeContainerSize();
      },

      // On client-side navigation the container is not laid out yet when the map is
      // created, so Leaflet sizes its tile grid from the wrong dimensions and leaves
      // grey gaps until it is told the real size.
      observeContainerSize () {
        this.resizeObserver = new ResizeObserver(() => {
          cancelAnimationFrame(this.idRafInvalidateSize);
          this.idRafInvalidateSize = requestAnimationFrame(() => this.map?.invalidateSize());
        });
        this.resizeObserver.observe(this.$el);
      },

      onBoundsChanged () {
        const MILLISEC_TIMEOUT_LOW = 500;
        const handler = () => {
          clearTimeout(this.idTimeoutBoundsChanged);
          this.idTimeoutBoundsChanged = setTimeout(() => {
            const bounds = this.map.getBounds();
            const [sw, ne] = [bounds.getSouthWest(), bounds.getNorthEast()];
            this.$emit('update:modelValue', [sw, ne].map(c => ({
              latitude: c.lat,
              longitude: c.lng,
            })));
          }, MILLISEC_TIMEOUT_LOW);
        };
        if (this.type === TYPE_OSM) {
          this.map.on('moveend', handler);
        }
      },
    },
  };

  /**
   * @type {Array<{latitude: number, longitude: number}>} a
   */
  function boundsValidator (a) {
    if (a.length !== 2) {
      return false;
    }
    if (typeof a[0] !== 'object' || typeof a[1] !== 'object') {
      return false;
    }
    const [
      {
        latitude: x1,
        longitude: y1,
      },
      {
        latitude: x2,
        longitude: y2,
      },
    ] = a;
    for (const coord of [x1, x2, y1, y2]) {
      if (typeof coord !== 'number') {
        return false;
      }
    }
    return true;
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.map {
  max-height: 550px;
  border-radius: 8px;
  background: grey;
}

.mapMarkerClustererCustom {
  font-size: 15px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1em;
  color: $color-white;
  border-radius: 50%;
  background: $color-brand-primary;
}

.openingHoursRow {
  display: flex;
  justify-content: space-between;
  width: calc(100% - 16px);
}

.addressRow,
.openingHoursRow,
.nameRow {
  margin: 2px 8px;
}

.addressRow {
  margin-bottom: 5px;
}

.nameRow {
  margin-bottom: 10px;
}

.storeInformation {
  position: absolute;
  z-index: 10000;
  height: 100%;
  background: white;
}
</style>

<style>
.leaflet-interactive {
  cursor: grab !important;
}
</style>
